// ─────────────────────────────────────────────────────────────────────────────
// graphLayoutRunner — run the layout in the worker, or on this thread if it
// cannot be reached.
//
// The fallback is NOT a silent one. Which path ran is reported alongside the
// timing, because "the layout took 40ms" means something completely different
// depending on whether it blocked the frame to do it.
//
// It lives beside graphLayout rather than inside it on purpose: the worker
// imports graphLayout, so a worker reference in that module would be a cycle.
// ─────────────────────────────────────────────────────────────────────────────

import { computeLayout, GraphPositions, Vec3 } from './graphLayout';
import { RexyGraphStore } from './graphStore';
import { RexyGraphNodeRecord } from './graphTypes';

export interface LayoutRun {
  positions: GraphPositions;
  /** Where it actually ran. Never inferred — set by the path that ran it. */
  ranIn: 'worker' | 'main';
  ms: number;
}

let worker: Worker | null = null;
let workerBroken = false;
let nextRequestId = 1;

function ensureWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./workers/graphLayout.worker.ts', import.meta.url));
    worker.onerror = () => {
      workerBroken = true;
      worker = null;
    };
    return worker;
  } catch (err) {
    // No worker support, or a bundler that could not produce one. Say so once.
    // eslint-disable-next-line no-console
    console.warn('[graphLayout] worker unavailable, laying out on the main thread:', err);
    workerBroken = true;
    return null;
  }
}

function snapshotNodes(store: RexyGraphStore): RexyGraphNodeRecord[] {
  const nodes: RexyGraphNodeRecord[] = [];
  store.forEachNode((n) => nodes.push(n));
  return nodes;
}

/**
 * Terminate the worker and let the next mount build a fresh one.
 *
 * The worker is a module singleton, so without this it outlives every unmount —
 * a thread still holding the last graph it was sent, for a surface the user has
 * navigated away from. Called from the lab's teardown, alongside the renderer's.
 */
export function disposeLayoutWorker(): void {
  if (!worker) return;
  worker.terminate();
  worker = null;
  workerBroken = false;
}

export function runLayout(store: RexyGraphStore, now: () => number): Promise<LayoutRun> {
  const w = ensureWorker();
  const started = now();

  if (!w) {
    const positions = computeLayout(store);
    return Promise.resolve({ positions, ranIn: 'main', ms: now() - started });
  }

  const requestId = nextRequestId++;
  return new Promise<LayoutRun>((resolve) => {
    const onMessage = (event: MessageEvent<any>) => {
      if (!event.data || event.data.requestId !== requestId) return;
      w.removeEventListener('message', onMessage);

      const ids: string[] = event.data.ids;
      const packed: Float32Array = event.data.packed;
      const positions: GraphPositions = new Map();
      for (let i = 0; i < ids.length; i++) {
        const v: Vec3 = [packed[i * 3], packed[i * 3 + 1], packed[i * 3 + 2]];
        positions.set(ids[i], v);
      }
      resolve({ positions, ranIn: 'worker', ms: now() - started });
    };

    w.addEventListener('message', onMessage);
    w.postMessage({ requestId, nodes: snapshotNodes(store) });
  });
}
